import {isFeatureEnabled} from '@github-ui/feature-flags'
import type React from 'react'
import {createContext, use, useMemo} from 'react'

import {useCopilotChatEntitlement} from '../../utils/copilot-chat-entitlement'
import {
  type CopilotChatEntitlementQuotas,
  CopilotLicenseType,
  type CopilotPlan,
  type UserLevelBudgetState,
} from '../../utils/copilot-chat-types'
import {copilotFeatureFlags} from '../../utils/copilot-feature-flags'
import {getTbbEntitlement} from '../../utils/token-based-billing-entitlement'

interface EntitlementContextType {
  licenseType: CopilotLicenseType | null
  plan: CopilotPlan | null
  chatQuotaRemaining: number
  premiumChatQuotaRemaining: number
  resetDate: string
  isLicensedLimited: boolean
  chatQuotaExceeded: boolean
  premiumInteractionsQuotaExceeded: boolean
  reloadQuota: (origin?: string) => void
  canPurchaseAdditionalQuota: boolean
  canUpgradePlan: boolean
  overagesEnabled: boolean
  eligibleForTrial: boolean
  sessionQuotaExceeded: boolean
  sessionQuotaRemaining: number | undefined
  sessionQuotaResetLabel: string
  weeklyQuotaExceeded: boolean
  weeklyQuotaRemaining: number | undefined
  weeklyQuotaResetLabel: string
  tbbQuotaExceeded: boolean
  userLevelBudgetEnabled: boolean
  userLevelBudgetState: UserLevelBudgetState | null
  userLevelBudgetPercentRemaining: number | null
  overagesRemaining: number | undefined
  overagesExceeded: boolean
  isIndividualPaidOrStudentPlan: boolean
  isCommercialPlan: boolean
}

interface EntitlementProviderProps {
  children: React.ReactNode
  initialLicenseType: CopilotLicenseType
  initialPlan?: CopilotPlan
  initialQuotas?: CopilotChatEntitlementQuotas
  hasCEorCBAccess?: boolean
}

export const EntitlementContext = createContext<EntitlementContextType | undefined>(undefined)

export function EntitlementProvider({
  children,
  initialLicenseType,
  initialPlan,
  initialQuotas,
  hasCEorCBAccess = false,
}: EntitlementProviderProps) {
  const [entitlement, reloadQuota] = useCopilotChatEntitlement(initialLicenseType, initialPlan, initialQuotas)
  const licenseType = entitlement.licenseType
  const plan = entitlement.plan ?? null

  const chatQuotaRemaining = entitlement.chatQuotaRemaining() ?? Infinity
  // When TBB is active and the server provides premiumInteractionsQuota, prefer its
  // percentRemaining over the legacy remaining.premiumInteractionsPercentage field.
  // The legacy field may be 0/absent even when the user still has quota.
  const premiumChatQuotaRemaining =
    (copilotFeatureFlags.copilotDotcomChatTbb && entitlement.quotas?.premiumInteractionsQuota?.percentRemaining != null
      ? entitlement.quotas.premiumInteractionsQuota.percentRemaining
      : entitlement.premiumInteractionsQuotaRemaining()) ?? Infinity

  const resetDate = entitlement.quotas?.resetDate
    ? new Date(entitlement.quotas.resetDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : ''

  // Feature flag to simulate quota exceeded state for local development
  const simulateQuotaExceeded = isFeatureEnabled('copilot_simulate_quota_exceeded')

  const isLicensedLimited = simulateQuotaExceeded || licenseType === CopilotLicenseType.LicensedLimited

  const canPurchaseAdditionalQuota = entitlement.canPurchaseAdditionalQuota()
  const canUpgradePlan = entitlement.canUpgradePlan()

  const {
    overagesEnabled,
    userLevelBudgetEnabled,
    userLevelBudgetState,
    userLevelBudgetPercentRemaining,
    sessionQuotaRemaining,
    sessionQuotaExceeded,
    sessionQuotaResetLabel,
    weeklyQuotaRemaining,
    weeklyQuotaExceeded,
    weeklyQuotaResetLabel,
    overagesRemaining,
    overagesExceeded,
    tbbQuotaExceeded,
    isIndividualPaidOrStudentPlan,
    isCommercialPlan,
  } = getTbbEntitlement({quotas: entitlement.quotas, plan, hasCEorCBAccess})

  const chatQuotaExceeded = simulateQuotaExceeded || (isLicensedLimited && chatQuotaRemaining <= 0)
  // CB/CE TBB users have their own banner flow; suppress the legacy premium-interactions banner for them.
  const premiumInteractionsQuotaExceeded =
    copilotFeatureFlags.premiumRequestQuotasEnabled &&
    !isLicensedLimited &&
    !userLevelBudgetEnabled &&
    premiumChatQuotaRemaining <= 0

  const eligibleForTrial = entitlement.eligibleForTrial()

  const value = useMemo(
    () => ({
      licenseType,
      chatQuotaRemaining,
      premiumChatQuotaRemaining,
      resetDate,
      isLicensedLimited,
      chatQuotaExceeded,
      premiumInteractionsQuotaExceeded,
      reloadQuota,
      plan,
      canPurchaseAdditionalQuota,
      canUpgradePlan,
      overagesEnabled,
      eligibleForTrial,
      sessionQuotaExceeded,
      sessionQuotaRemaining,
      sessionQuotaResetLabel,
      weeklyQuotaExceeded,
      weeklyQuotaRemaining,
      weeklyQuotaResetLabel,
      overagesRemaining,
      overagesExceeded,
      tbbQuotaExceeded,
      userLevelBudgetEnabled,
      userLevelBudgetState,
      userLevelBudgetPercentRemaining,
      isIndividualPaidOrStudentPlan,
      isCommercialPlan,
    }),
    [
      licenseType,
      chatQuotaRemaining,
      premiumChatQuotaRemaining,
      resetDate,
      isLicensedLimited,
      chatQuotaExceeded,
      premiumInteractionsQuotaExceeded,
      reloadQuota,
      plan,
      canPurchaseAdditionalQuota,
      canUpgradePlan,
      overagesEnabled,
      eligibleForTrial,
      sessionQuotaExceeded,
      sessionQuotaRemaining,
      sessionQuotaResetLabel,
      weeklyQuotaExceeded,
      weeklyQuotaRemaining,
      weeklyQuotaResetLabel,
      overagesRemaining,
      overagesExceeded,
      tbbQuotaExceeded,
      userLevelBudgetEnabled,
      userLevelBudgetState,
      userLevelBudgetPercentRemaining,
      isIndividualPaidOrStudentPlan,
      isCommercialPlan,
    ],
  )

  return <EntitlementContext value={value}>{children}</EntitlementContext>
}

export const useEntitlement = () => {
  const context = use(EntitlementContext)
  if (!context) {
    throw new Error('useEntitlement must be used within EntitlementProvider')
  }
  return context
}
